import Common from '@/libs/common';
import { PickupDropoffType, type Stop, type Trip } from '@/@types/gtfs-ressource';

/** A passenger walks 1 km in 15 min: the pace of a crow-fly estimate. A measured walk takes the IGN's own duration. */
export const WALK_SECONDS_PER_METER = (15 * 60) / 1000;
/** Crow-fly distances are stretched by this factor to approximate the walk through the streets. */
export const WALK_DETOUR_FACTOR = 1.5;
export const MAX_TRANSFER_WALK_M = 400;
export const MAX_ACCESS_WALK_M = 2000;
/** Shortest change between two vehicles, at the same stop or between quays of a merged stop. */
export const MIN_TRANSFER_SECONDS = 60;

/** A trip of a given service day: the same trip_id runs on several days, the key tells them apart. */
export interface NetworkTrip {
  key: string;
  trip: Trip;
  serviceDate: string;
  /** Scheduled times, in epoch seconds, one per stop of the pattern. */
  arrivals: number[];
  departures: number[];
}

/** Trips of one route calling at the same stops, in the same order, with the same pickup rules. */
export interface Pattern {
  routeId: string;
  stops: number[];
  canBoard: boolean[];
  canAlight: boolean[];
  /** Sorted by departure from the first stop. */
  trips: NetworkTrip[];
}

export interface Footpath {
  to: number;
  /** Walking time, 0 between quays of the same merged stop. */
  walkSeconds: number;
  distance: number;
}

export interface Network {
  stopIds: string[];
  stopIndex: Map<string, number>;
  stops: Stop[];
  patterns: Pattern[];
  /** For each stop, the patterns calling there with the stop's position in each. */
  stopPatterns: Array<Array<{ pattern: number; position: number }>>;
  footpaths: Footpath[][];
  tripsByKey: Map<string, { pattern: number; index: number }>;
}

export interface NetworkInput {
  stops: { [stopId: string]: Stop };
  /** Trips of the day and of the day before, each with the `midnight` of its own service day. */
  tripsByServiceDate: Array<{ serviceDate: string; trips: Trip[] }>;
  canonicalOf: { [stopId: string]: string };
}

/** Estimated walk from a crow-fly distance. */
export function walkSeconds(distance: number): number {
  return Math.round(distance * WALK_DETOUR_FACTOR * WALK_SECONDS_PER_METER);
}

export function stopDistance(a: Stop, b: Stop): number {
  return Common.coordDistance(
    { latitude: a.stop_lat, longitude: a.stop_lon },
    { latitude: b.stop_lat, longitude: b.stop_lon },
  );
}

export function tripKey(tripId: string, serviceDate: string): string {
  return `${tripId}@${serviceDate}`;
}

export function buildNetwork({ stops, tripsByServiceDate, canonicalOf }: NetworkInput): Network {
  const stopIds: string[] = [];
  const stopIndex = new Map<string, number>();
  const indexOf = (stopId: string): number | undefined => {
    let index = stopIndex.get(stopId);
    if (index === undefined && stops[stopId]) {
      index = stopIds.length;
      stopIds.push(stopId);
      stopIndex.set(stopId, index);
    }
    return index;
  };

  const patternsByKey = new Map<string, Pattern>();
  tripsByServiceDate.forEach(({ serviceDate, trips }) => {
    trips.forEach(trip => {
      const stopTimes = [...trip.stop_times]
        .sort((a, b) => a.stop_sequence - b.stop_sequence)
        .filter(st => stops[st.stop_id]);
      if (stopTimes.length < 2) return;

      const patternStops = stopTimes.map(st => indexOf(st.stop_id)!);
      const canBoard = stopTimes.map(st => st.pickup_type !== PickupDropoffType.NOT_ALLOWED);
      const canAlight = stopTimes.map(st => st.drop_off_type !== PickupDropoffType.NOT_ALLOWED);
      const key = [
        trip.route_id,
        ...stopTimes.map((st, i) => `${st.stop_id}:${+canBoard[i]}${+canAlight[i]}`),
      ].join('|');

      let pattern = patternsByKey.get(key);
      if (!pattern) {
        pattern = { routeId: trip.route_id, stops: patternStops, canBoard, canAlight, trips: [] };
        patternsByKey.set(key, pattern);
      }
      pattern.trips.push({
        key: tripKey(trip.trip_id, serviceDate),
        trip: { ...trip, stop_times: stopTimes },
        serviceDate,
        arrivals: stopTimes.map(st => trip.midnight + st.arrival_time),
        departures: stopTimes.map(st => trip.midnight + st.departure_time),
      });
    });
  });

  const patterns = [...patternsByKey.values()];
  const stopPatterns: Network['stopPatterns'] = stopIds.map(() => []);
  const tripsByKey: Network['tripsByKey'] = new Map();
  patterns.forEach((pattern, patternIndex) => {
    pattern.trips.sort((a, b) => a.departures[0] - b.departures[0]);
    pattern.trips.forEach((trip, index) => tripsByKey.set(trip.key, { pattern: patternIndex, index }));
    pattern.stops.forEach((stop, position) => stopPatterns[stop].push({ pattern: patternIndex, position }));
  });

  const networkStops = stopIds.map(stopId => stops[stopId]);
  return {
    stopIds,
    stopIndex,
    stops: networkStops,
    patterns,
    stopPatterns,
    footpaths: buildFootpaths(networkStops, canonicalOf),
    tripsByKey,
  };
}

/** Links between stops within walking range, found with a sweep over the stops sorted by latitude. */
function buildFootpaths(stops: Stop[], canonicalOf: { [stopId: string]: string }): Footpath[][] {
  const footpaths: Footpath[][] = stops.map(() => []);
  // 1° of latitude is about 111 km, whatever the longitude
  const maxDeltaLat = (MAX_TRANSFER_WALK_M / 111_000) * 1.01;
  const order = stops.map((_, i) => i).sort((a, b) => stops[a].stop_lat - stops[b].stop_lat);
  const canonical = (stop: Stop) => canonicalOf[stop.stop_id] ?? stop.stop_id;

  for (let i = 0; i < order.length; i++) {
    const a = stops[order[i]];
    for (let j = i + 1; j < order.length; j++) {
      const b = stops[order[j]];
      if (b.stop_lat - a.stop_lat > maxDeltaLat) break;
      const sameStop = canonical(a) === canonical(b);
      const distance = stopDistance(a, b);
      if (!sameStop && distance > MAX_TRANSFER_WALK_M) continue;
      const walk = sameStop ? 0 : walkSeconds(distance);
      const linkDistance = sameStop ? 0 : distance;
      footpaths[order[i]].push({ to: order[j], walkSeconds: walk, distance: linkDistance });
      footpaths[order[j]].push({ to: order[i], walkSeconds: walk, distance: linkDistance });
    }
  }
  return footpaths;
}

const NETWORK_CACHE_SIZE = 2;
const networkCache = new Map<string, Network>();

/** The network of a GTFS file on a given day, built once: the build is the costly part of a search. */
export function cachedNetwork(gtfsId: string, dateGtfs: string, build: () => Network): Network {
  const key = `${gtfsId}@${dateGtfs}`;
  const cached = networkCache.get(key);
  if (cached) return cached;
  const network = build();
  networkCache.set(key, network);
  if (networkCache.size > NETWORK_CACHE_SIZE) networkCache.delete(networkCache.keys().next().value!);
  return network;
}

export function clearNetworkCache() {
  networkCache.clear();
}
